// ═══════════════════════════════════════════════════════════════════════════════
// src/auth/AuthProvider.jsx
// React context for Supabase Auth. Wraps <App /> and exposes:
//   { user, profile, practiceId, role, loading, error, signIn, signOut }
// via the useAuth() hook.
//
// Reads role + practice_id from auth.user.app_metadata (set server-side via
// admin API). Falls back to the public.users row if app_metadata is missing.
//
// HANG-RESISTANT BOOTSTRAP: every async branch is wrapped in try/finally so
// setLoading(false) always runs, plus a 6-second hard timeout fallback. Audit
// log calls are fire-and-forget so they cannot block the auth flow.
// ═══════════════════════════════════════════════════════════════════════════════

import { createContext, useContext, useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase, signInWithEmail, signOut as sbSignOut, logAudit, logFailedLogin } from "../lib/supabaseClient";
import { clearAllFieldDrafts, sweepExpiredDrafts } from "../lib/chwFieldCore";
import { clearLocalPhi } from "../lib/localPhi";
import { applyBrandPalette, DEFAULT_BRAND_PALETTE } from "../lib/brandPalette";
import { rememberLoginPalette, rememberLoginPractice } from "../lib/loginTint";
import { readViewAs, readSpectator, writeViewAs, writeSpectator, effectiveOverrides, clearForeignOverrides } from "./identityOverrides";
import { classifyAuthEvent } from "./authEvents";

// sessionStorage, or null where the browser blocks it (private mode, sandbox).
function sessionStore() {
  try { return window.sessionStorage; } catch { return null; }
}

// Which NC Medicaid designations deliver which program. Mirrors
// practice_supports_amh() / practice_supports_tcm() in the database. A CIN
// appears in both because a network spans member practices with different
// designations. A Set is used so an undefined or null designation returns
// false without a guard.
const AMH_PROGRAMS = new Set(["AMH Tier 3", "AMH+", "CIN"]);
const TCM_PROGRAMS = new Set(["AMH+", "CMA", "CIN"]);

const AuthCtx = createContext(null);
const BOOTSTRAP_TIMEOUT_MS = 6000;
// Longest sign-out waits for the Logout audit write before ending the session.
const LOGOUT_AUDIT_WAIT_MS = 3000;

export function useAuth() {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}

export function AuthProvider({ children }) {
  // CHW engagement drafts past their 12h life go on app start, for any user.
  useEffect(() => { sweepExpiredDrafts(); }, []);
  // Server-state cache. Cleared on EVERY sign-out path (explicit and
  // external) so a shared workstation never hands the next user a prior
  // session's cached PHI. See clearServerState below.
  const queryClient = useQueryClient();
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  // Resolved super admin status + capability map. Loaded alongside profile.
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);

  // The user id this tab holds: undefined until bootstrap or INITIAL_SESSION
  // says, then a uid or null. Tells a real sign-in from supabase-js
  // re-announcing the same session (SIGNED_IN on every tab wake-up); see
  // authEvents.js.
  const knownUidRef = useRef(undefined);
  // True from the start of signOut() until it finishes. Reads started in that
  // window would go out after the session is gone, with only the anon key.
  const signingOutRef = useRef(false);

  // "View as role": preview what another role sees, inside your OWN practice
  // with your OWN identity. It can only NARROW the interface - RLS still runs
  // on the real user's JWT, so a preview cannot grant access you did not have.
  //
  // This PR adds the override only. Read-only enforcement is PR 3; until then
  // a preview is a lens, not a guardrail, and the sheet says so.
  //
  // Stored as { uid, role } and honored only for that uid (see
  // identityOverrides.js): a preview must never follow the tab to another
  // account, such as a patient signing in after the staff session ended.
  const [viewAs, setViewAsState] = useState(() => readViewAs(sessionStore()));
  const sessionUid = session?.user?.id || null;
  const setViewAsRole = useCallback((nextRole) => {
    writeViewAs(sessionStore(), sessionUid, nextRole || null);
    setViewAsState(sessionUid && nextRole ? { uid: sessionUid, role: nextRole } : null);
  }, [sessionUid]);
  const [capabilities, setCapabilities] = useState(null);
  // Spectator mode: when set, super admin is "viewing as" a practice's owner.
  // Read-only - all writes are blocked at the helper level (see lib/db.js).
  // Stored in sessionStorage so a page reload preserves spectator mode within
  // the same tab but a new tab starts clean.
  // Stamped with the super admin's uid and honored only for that uid.
  const [spectator, setSpectator] = useState(() => readSpectator(sessionStore()));

  // Bumped to re-read capabilities without a new profile object: on tab focus
  // and on refreshProfile() (round 4 #3; see the capabilities effect below).
  const [capsNonce, setCapsNonce] = useState(0);
  // The profile read in flight, so the bootstrap read and the INITIAL_SESSION
  // event that arrives while it runs share ONE users + super_admins read
  // instead of two (round 4 #3: they made every page load ask twice).
  const profileReadRef = useRef(null); // { uid, promise }

  // Load public.users row matching auth.user.id. Never throws.
  const readProfile = useCallback(async (userId) => {
    if (!userId) { setProfile(null); return; }
    try {
      const { data, error: pErr } = await supabase
        .from("users")
       .select("id, practice_id, email, full_name, role, provider_id, patient_id, avatar_url, is_active, practices(name, subscription_tier, brand_palette, cm_program_type, timezone)")
        .eq("id", userId)
        .maybeSingle();
      if (pErr) {
        console.warn("[PracticeOS] profile load failed:", pErr.message);
        setProfile(null);
      } else {
        // Keep the same object when nothing changed (a focus re-read), so
        // everything keyed on the profile does not run again for nothing.
        setProfile((prev) => (prev && data && JSON.stringify(prev) === JSON.stringify(data) ? prev : data));
      }
    } catch (e) {
      console.warn("[PracticeOS] profile load threw:", e?.message || e);
      setProfile(null);
    }

    // Signed out while the profile was loading: the session is gone, so the
    // next read would only earn a 401.
    if (signingOutRef.current) return;

    // Resolve super admin status and capabilities in parallel.
    // Both are non-blocking: failures default to safe values (no admin, no caps).
    try {
      const { data: saRows, error: saErr } = await supabase
        .from("super_admins")
        .select("id")
        .eq("user_id", userId)
        .is("revoked_at", null)
        .limit(1);
      // Fail closed: the catch below warns and sets isSuperAdmin=false.
      if (saErr) throw saErr;
      setIsSuperAdmin(!!(saRows && saRows.length > 0));
    } catch (e) {
      console.warn("[PracticeOS] super_admin check failed:", e?.message || e);
      setIsSuperAdmin(false);
    }
  }, []);
  const loadProfile = useCallback((userId) => {
    const cur = profileReadRef.current;
    if (userId && cur && cur.uid === userId) return cur.promise;
    const entry = { uid: userId, promise: null };
    entry.promise = readProfile(userId).finally(() => {
      if (profileReadRef.current === entry) profileReadRef.current = null;
    });
    profileReadRef.current = entry;
    return entry.promise;
  }, [readProfile]);

  // Initial session + subscribe to future changes ------------------------------
  useEffect(() => {
    let active = true;

    // Safety net: if anything hangs, force loading off after 6s so the UI
    // can at least render the login screen instead of a forever spinner.
    const failSafe = setTimeout(() => {
      if (active) {
        console.warn("[PracticeOS] bootstrap timeout - forcing loading=false");
        setLoading(false);
      }
    }, BOOTSTRAP_TIMEOUT_MS);

    (async () => {
      try {
        const { data, error: sErr } = await supabase.auth.getSession();
        if (!active) return;
        if (sErr) console.warn("[PracticeOS] getSession error:", sErr.message);
        const initial = data?.session || null;
        knownUidRef.current = initial?.user?.id || null;
        setSession(initial);
        if (initial?.user) {
          await loadProfile(initial.user.id);
        }
      } catch (e) {
        if (!active) return;
        console.error("[PracticeOS] bootstrap failed:", e?.message || e);
        setError(e?.message || "Auth bootstrap failed");
      } finally {
        if (active) {
          clearTimeout(failSafe);
          setLoading(false);
        }
      }
    })();

    const { data: listener } = supabase.auth.onAuthStateChange((event, newSession) => {
      if (!active) return;
      const nextUid = newSession?.user?.id || null;
      const kind = classifyAuthEvent(event, knownUidRef.current, nextUid);
      if (kind.signedOut) {
        // SIGNED_OUT, or any event that no longer carries a user.
        knownUidRef.current = null;
        setSession(null);
        setProfile(null);
        // Same PHI hygiene as the explicit signOut() path: an externally
        // ended session (expiry, another tab, admin revocation) must not
        // leave cached server state behind, nor patient data in browser
        // storage (note drafts, recent members; see lib/localPhi.js).
        queryClient.clear();
        clearAllFieldDrafts();   // CHW engagement drafts kept on this device
        clearLocalPhi();
        return;
      }
      // Mid sign-out, a re-announced session (a tab wake-up firing at the
      // same moment as the idle sign-out) must not start new reads.
      if (signingOutRef.current) return;
      knownUidRef.current = nextUid;
      setSession(newSession);
      if (kind.reloadProfile) {
        // Background: don't block UI
        loadProfile(nextUid).catch((e) =>
          console.warn("[PracticeOS] profile load failed:", e?.message)
        );
      }
      // Only a real sign-in is audited as a Login, not supabase-js
      // re-announcing the same session when the tab wakes (authEvents.js).
      if (kind.isLogin) {
        logAudit({
          action: "Login",
          entityType: "session",
          entityId: nextUid,
        }).catch(() => {});
      }
    });

    return () => {
      active = false;
      clearTimeout(failSafe);
      listener.subscription.unsubscribe();
    };
  }, [loadProfile, queryClient]);

  // Spectator mode actions ------------------------------------------------------
  const enterSpectator = useCallback(async (practiceId, reason = null) => {
    if (!isSuperAdmin) throw new Error("Spectator mode requires super admin access");
    // Resolve the spectated practice's name + capability snapshot
    const [practiceRes, capRes, ownerRes] = await Promise.all([
      supabase.from("practices").select("id, name, subscription_tier, brand_palette, cm_program_type").eq("id", practiceId).single(),
      supabase.rpc("get_effective_capabilities", { p_practice_id: practiceId }),
      // Find the practice's primary owner to determine the role we're acting as
      supabase.from("users").select("id, email, full_name, role").eq("practice_id", practiceId).eq("role", "Owner").eq("is_active", true).limit(1).maybeSingle(),
    ]);
    if (practiceRes.error) throw practiceRes.error;
    if (capRes.error)      throw capRes.error;
    const sess = {
      practice_id:   practiceRes.data.id,
      practice_name: practiceRes.data.name,
      tier:          practiceRes.data.subscription_tier,
      brand_palette: practiceRes.data.brand_palette,
      cm_program:    practiceRes.data.cm_program_type,
      capabilities:  capRes.data,
      acting_role:   ownerRes.data?.role  || "Owner",
      acting_email:  ownerRes.data?.email || null,
      acting_name:   ownerRes.data?.full_name || null,
      entered_at:    new Date().toISOString(),
      // Owner of this override: it applies to this account only.
      uid:           session?.user?.id || null,
    };
    writeSpectator(sessionStore(), sess);
    setSpectator(sess);
    // Audit the entry. Failure does not block - we'd rather have spectator mode
    // active than blocked by an audit hiccup, but we log a warning.
    // Note: Supabase's rpc() returns a thenable, not a true Promise, so we
    // wrap the call in an async IIFE before using try/catch instead of
    // chaining .catch() directly (which throws "rpc(...).catch is not a function").
    (async () => {
      try {
        const { error: auditErr } = await supabase.rpc("log_spectator_event", { p_practice_id: practiceId, p_event: "enter", p_reason: reason });
        if (auditErr) console.warn("[PracticeOS] spectator entry audit failed:", auditErr.message);
      } catch (e) {
        console.warn("[PracticeOS] spectator entry audit failed:", e?.message);
      }
    })();
  }, [isSuperAdmin, session]);

  const exitSpectator = useCallback(async () => {
    if (!spectator) return;
    const practiceId = spectator.practice_id;
    writeSpectator(sessionStore(), null);
    setSpectator(null);
    (async () => {
      try {
        const { error: auditErr } = await supabase.rpc("log_spectator_event", { p_practice_id: practiceId, p_event: "exit" });
        if (auditErr) console.warn("[PracticeOS] spectator exit audit failed:", auditErr.message);
      } catch (e) {
        console.warn("[PracticeOS] spectator exit audit failed:", e?.message);
      }
    })();
  }, [spectator]);

  // Re-fetch the spectated practice's tier + capability snapshot in place. Used
  // after a super admin changes a practice while already viewing it, and by the
  // window-focus refresher below, so the effective tier the app sees stays in
  // sync without exiting + re-entering View As.
  const refreshSpectator = useCallback(async () => {
    if (!spectator?.practice_id) return;
    try {
      const [practiceRes, capRes] = await Promise.all([
        supabase.from("practices").select("id, name, subscription_tier, brand_palette, cm_program_type").eq("id", spectator.practice_id).single(),
        supabase.rpc("get_effective_capabilities", { p_practice_id: spectator.practice_id }),
      ]);
      if (practiceRes.error) return;
      const next = {
        ...spectator,
        practice_name: practiceRes.data.name,
        tier:          practiceRes.data.subscription_tier,
        brand_palette: practiceRes.data.brand_palette,
        capabilities:  capRes.error ? spectator.capabilities : capRes.data,
      };
      writeSpectator(sessionStore(), next);
      setSpectator(next);
    } catch (e) {
      console.warn("[PracticeOS] spectator refresh failed:", e?.message);
    }
  }, [spectator]);

  // Re-fetch the signed-in user's profile (which embeds the practice's tier and
  // brand palette via the practices(...) select). Lets a tier or brand change
  // land in the live session without a full re-login. Also refreshes the
  // spectator snapshot when View As is active.
  const refreshProfile = useCallback(async () => {
    const uid = session?.user?.id;
    if (uid) await loadProfile(uid);
    setCapsNonce((n) => n + 1);
    if (spectator?.practice_id) await refreshSpectator();
  }, [session, loadProfile, spectator, refreshSpectator]);

  // Eventual consistency: when the tab regains focus or becomes visible,
  // re-read the profile (and spectator snapshot) so a plan/brand change made
  // elsewhere - e.g. a super admin flipping this practice's tier in the admin
  // tab - shows up without a re-login. Two indexed reads, only on focus.
  useEffect(() => {
    const onFocus = () => {
      if (signingOutRef.current) return;
      const uid = session?.user?.id;
      if (uid) loadProfile(uid).catch(() => {});
      if (uid) setCapsNonce((n) => n + 1);
      if (spectator?.practice_id) refreshSpectator().catch(() => {});
    };
    const onVis = () => { if (document.visibilityState === "visible") onFocus(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVis);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [session, loadProfile, spectator, refreshSpectator]);

  // Actions --------------------------------------------------------------------
  const signIn = useCallback(async (email, password) => {
    setError(null);
    try {
      await signInWithEmail(email, password);
    } catch (e) {
      setError(e.message || "Sign in failed");
      // Signed out here, so not logAudit (authenticated only): see
      // logFailedLogin in supabaseClient.js.
      logFailedLogin({ email, error: e.message }).catch(() => {});
      throw e;
    }
  }, []);

  const signOut = useCallback(async () => {
    signingOutRef.current = true;
    try {
      if (session?.user) {
        // Wait for the audit write (bounded) BEFORE ending the session. Fired
        // without waiting, it raced supabase.auth.signOut(), went out with only
        // the anon key and was rejected (401), so no Logout row was ever
        // written. The cap keeps a slow network from holding the user on the
        // page; the smoke journey "sign out writes the Logout audit" guards
        // the ordering.
        await Promise.race([
          logAudit({
            action: "Logout",
            entityType: "session",
            entityId: session.user.id,
          }).catch(() => null),
          new Promise((resolve) => setTimeout(resolve, LOGOUT_AUDIT_WAIT_MS)),
        ]);
      }
      try { await sbSignOut(); } catch (e) { console.warn("[PracticeOS] signOut error:", e?.message); }
      // Drop all cached server state (react-query). Without this, a shared
      // workstation could serve the previous user's cached rows (addons,
      // caseload, chart data) to the next sign-in until they went stale.
      queryClient.clear();
      clearAllFieldDrafts();   // CHW engagement drafts kept on this device
      // Patient data kept in browser storage (an unsigned note draft, recent
      // member chips) must not outlive the session on a shared workstation.
      clearLocalPhi();
      setProfile(null);
      setSession(null);
      setIsSuperAdmin(false);
      setCapabilities(null);
      clearForeignOverrides(sessionStore(), null);
      setSpectator(null);
      setViewAsState(null);
      knownUidRef.current = null;
    } finally {
      signingOutRef.current = false;
    }
  }, [session, queryClient]);

  // Drop stored overrides that belong to another account (or to nobody, once
  // signed out). Runs on every identity change after bootstrap, which covers
  // the paths signOut() does not: an expired session, a sign-out in another
  // tab, and a different account signing in from another tab. The derived
  // value below already ignores foreign entries; this keeps sessionStorage
  // (read directly by the spectator write guard in lib/db.js) in step.
  useEffect(() => {
    if (loading) return;
    const uid = session?.user?.id || null;
    if (clearForeignOverrides(sessionStore(), uid)) {
      setViewAsState(readViewAs(sessionStore()));
      setSpectator(readSpectator(sessionStore()));
    }
  }, [session, loading]);

  // Load capabilities whenever practice changes (resolved server-side via RPC).
  // Keyed on the practice id and capsNonce (bumped on focus and by
  // refreshProfile, so a plan change lands), not the session object:
  // supabase-js hands over a new session object on every token refresh and
  // tab wake-up. Round 4 #3: no longer keyed on the profile OBJECT, which
  // changed twice during sign-in and loaded capabilities three times on
  // every page load.
  const capsPracticeId = session?.user?.app_metadata?.practice_id || profile?.practice_id || null;
  useEffect(() => {
    const pid = capsPracticeId;
    if (!pid || signingOutRef.current) { setCapabilities(null); return; }
    let cancelled = false;
    supabase.rpc("get_effective_capabilities", { p_practice_id: pid })
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) { console.warn("[PracticeOS] capabilities load failed:", error.message); setCapabilities(null); }
        else setCapabilities(data);
      });
    return () => { cancelled = true; };
  }, [capsPracticeId, capsNonce]);

  // Apply brand color palette to <html> CSS variables whenever the effective
  // practice changes. Spectator mode wins (super admin sees the spectated
  // practice's brand). Otherwise uses the signed-in user's practice brand.
  // Falls back to DEFAULT_BRAND_PALETTE (teal) on logout / pre-auth screens.
  useEffect(() => {
    if (spectator?.brand_palette) {
      applyBrandPalette(spectator.brand_palette);
    } else if (profile?.practices?.brand_palette) {
      applyBrandPalette(profile.practices.brand_palette);
      // Sign-in screen tint for the next visit on this device (palette key
      // only). Not done for super-admin spectator mode above.
      rememberLoginPalette(profile.practices.brand_palette);
      rememberLoginPractice(profile.practices.name);
    } else {
      applyBrandPalette(DEFAULT_BRAND_PALETTE);
    }
  }, [profile, spectator]);

  // Derived values --------------------------------------------------------------
  const value = useMemo(() => {
    const md = session?.user?.app_metadata || {};
    const realRole = md.role || profile?.role || null;
    // Only overrides set by THIS account apply (identityOverrides.js): a role
    // preview or spectator session left in the tab by a previous account
    // must not hand the next one (a patient, say) the staff shell.
    // A preview also applies only to an Owner or a super admin (who can pick
    // one); for anyone else a leftover value is ignored.
    const { viewAsRole, spectator: spec } = effectiveOverrides({
      uid: session?.user?.id || null, realRole, isSuperAdmin, viewAs, spectator,
    });
    return {
      user:            session?.user || null,
      session,
      profile,
      // When spectator mode is active, the *effective* identity reported by
      // useAuth() is the spectated practice's. Components that gate on tier,
      // role, practiceId, or capabilities transparently see the spectated
      // values. Real super admin identity is preserved on session.user for
      // audit logging and for the spectator banner / exit button.
      // Spectator (another practice) wins over a role preview (this practice).
      role:            spec ? spec.acting_role : (viewAsRole || md.role || profile?.role || null),
      realRole,
      viewAsRole,
      setViewAsRole,
      practiceId:      spec ? spec.practice_id  : (md.practice_id || profile?.practice_id || null),
      patientId:       md.patient_id  || profile?.patient_id  || null,
      providerId:      md.provider_id || profile?.provider_id || null,
      tier:            spec ? spec.tier         : (profile?.practices?.subscription_tier || "Lite"),
      practiceName:    spec ? spec.practice_name : (profile?.practices?.name || null),
      // The practice's NC Medicaid care management designation - a FACT about
      // the practice, distinct from what it has purchased. NULL means it does
      // not deliver care management at all.
      //
      // These mirror practice_supports_amh() / practice_supports_tcm() in the
      // database. They are convenience predicates for rendering only; the DB
      // functions remain the authority for anything that gates data access.
      cmProgram:       spec ? (spec.cm_program || null) : (profile?.practices?.cm_program_type || null),
      supportsAmh:     AMH_PROGRAMS.has(spec ? spec.cm_program : profile?.practices?.cm_program_type),
      supportsTcm:     TCM_PROGRAMS.has(spec ? spec.cm_program : profile?.practices?.cm_program_type),
      capabilities:    spec ? spec.capabilities : capabilities,
      isSuperAdmin,
      spectator:       spec,
      enterSpectator,
      exitSpectator,
      refreshProfile,
      loading,
      error,
      isAuthenticated: !!session,
      signIn,
      signOut,
    };
  }, [session, profile, capabilities, isSuperAdmin, spectator, enterSpectator, exitSpectator, viewAs, setViewAsRole, refreshProfile, loading, error, signIn, signOut]);

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}
